import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSmartBack } from "../../hooks/useSmartBack";
import { findTextById, updateText, findTextBySlugAndDate, toggleReadStatus, toSortedArray } from "../../services/workspaceService";
import { findTextInWorkspace, textReaderPath, navigateToText } from "../../utils/textRoutes";
import { rememberWritingText } from "../../utils/writingSession";
import { useMobile } from "../../hooks/useMobile";
import { ArrowLeft } from "lucide-react";
import { useWorkspace } from "../../context/WorkspaceContext";
import TextReader from "../workspace/TextReader";
import BackgroundImage from "../common/BackgroundImage";
import { BACKGROUNDS } from "../../config/backgrounds";
import deskGlass from "../../styles/deskGlass";

export default function ArticleView() {
  const { deskSlug, drawerSlug, notebookSlug, textId, date, slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = useSmartBack();
  const isMobile = useMobile();
  const { workspace, loading: wsLoading, setPath } = useWorkspace();
  const [text, setText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const readingMode = Boolean(location.state?.readingMode);
  const queue = location.state?.queue || [];
  const queueIndex = location.state?.queueIndex ?? -1;
  const returnTo = location.state?.returnTo || "/read";

  const queueMeta = useMemo(() => {
    if (!readingMode || queueIndex < 0 || !queue.length) return null;
    return { index: queueIndex, total: queue.length };
  }, [readingMode, queueIndex, queue.length]);

  const advanceQueue = useCallback(() => {
    if (!readingMode || queueIndex < 0 || !queue.length) {
      navigate(returnTo);
      return;
    }
    const nextIdx = queueIndex + 1;
    if (nextIdx >= queue.length) {
      navigate(returnTo);
      return;
    }
    const nextId = queue[nextIdx];
    let nextText = null;
    for (const desk of workspace?.desks || []) {
      for (const drawer of toSortedArray(desk.drawers)) {
        for (const notebook of toSortedArray(drawer.notebooks)) {
          for (const t of toSortedArray(notebook.texts)) {
            if (t.id === nextId) {
              nextText = {
                ...t,
                deskId: desk.id,
                deskSlug: desk.slug,
                drawerId: drawer.id,
                drawerSlug: drawer.slug,
                notebookId: notebook.id,
                notebookSlug: notebook.slug,
              };
              break;
            }
          }
          if (nextText) break;
        }
        if (nextText) break;
      }
      if (nextText) break;
    }
    if (nextText) {
      navigate(textReaderPath(nextText), {
        state: { readingMode: true, queue, queueIndex: nextIdx, returnTo },
        replace: true,
      });
    } else {
      navigate(returnTo);
    }
  }, [readingMode, queue, queueIndex, returnTo, navigate, workspace]);

  const handleReadingAction = useCallback(
    (action) => {
      if (action === "next") {
        advanceQueue();
        return;
      }
      if (action === "read" || action === "archive" || action === "trash") {
        setText((prev) => (prev ? { ...prev, isRead: action === "read" || action === "archive" } : prev));
      }
      const leavesQueue = ["read", "later", "archive", "trash", "review"].includes(action);
      if (!leavesQueue || !readingMode) return;
      window.setTimeout(() => {
        if (queue.length > 0) advanceQueue();
        else navigate(returnTo, { replace: true });
      }, 350);
    },
    [advanceQueue, readingMode, queue.length, navigate, returnTo]
  );

  useEffect(() => {
    let cancelled = false;

    async function resolveText() {
      setLoading(true);
      setNotFound(false);
      setLoadError(null);

      try {
        let found = null;
        if (workspace?.desks?.length) {
          if (date && slug) {
            found = findTextInWorkspace(workspace, { slug, textId: slug });
          } else {
            found = findTextInWorkspace(workspace, {
              textId,
              deskSlug,
              drawerSlug,
              notebookSlug,
              slug: textId,
            });
          }
        }

        if (!found) {
          if (date && slug) {
            found = await findTextBySlugAndDate(slug, date);
          } else if (textId) {
            found = await findTextById(textId);
          }
        }

        if (cancelled) return;

        if (found) {
          setText(found);
          setPath([
            { type: "desk", id: found.deskId, name: found.deskName || "Escritorio" },
            { type: "drawer", id: found.drawerId, name: found.drawerName || "Cajón" },
            { type: "notebook", id: found.notebookId, name: found.notebookName || "Cuaderno" },
          ]);

          rememberWritingText(found);

          const history = JSON.parse(sessionStorage.getItem("nav_history") || "[]");
          const next = [{ id: found.id, title: found.title, timestamp: Date.now() }, ...history.filter((h) => h.id !== found.id)].slice(0, 5);
          sessionStorage.setItem("nav_history", JSON.stringify(next));
          setNotFound(false);
        } else if (!wsLoading) {
          setText(null);
          setNotFound(true);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[ArticleView] cloud lookup failed:", err);
          setText(null);
          setNotFound(false);
          setLoadError(err?.message || "No se pudo verificar el artículo en la nube.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (workspace || !wsLoading) {
      resolveText();
    }

    return () => {
      cancelled = true;
    };
  }, [workspace, wsLoading, textId, date, slug, deskSlug, drawerSlug, notebookSlug, setPath, retryKey]);

  const handleSaveTranslation = async (id, targetLang, translation) => {
    if (!text) return;
    try {
      await updateText(text.deskId, text.drawerId, text.notebookId, id, {
        [`translations/${targetLang}`]: translation,
      });
    } catch (err) {
      console.error("Translation cache save failed:", err);
    }
  };

  const handleToggleRead = async (dId, drId, nId, tId) => {
    if (!text) return;
    await toggleReadStatus(dId, drId, nId, tId);
    setText((prev) => ({ ...prev, isRead: !prev.isRead }));
  };

  if (loading || wsLoading) {
    return <div style={{ color: "#D4AF37", padding: "40px", textAlign: "center" }}>Cargando artículo...</div>;
  }

  if (loadError) {
    return (
      <div style={{ color: "#D4AF37", padding: "40px", textAlign: "center" }}>
        <p>No pude verificar el artículo en la nube.</p>
        <p style={{ color: "rgba(249,246,238,0.7)", fontSize: "0.9rem" }}>
          Esto no significa que se haya perdido.
        </p>
        <button
          type="button"
          onClick={() => setRetryKey((key) => key + 1)}
          style={{
            marginTop: "16px",
            padding: "10px 20px",
            borderRadius: "8px",
            border: "1px solid rgba(212,175,55,0.4)",
            background: "rgba(212,175,55,0.15)",
            color: "#D4AF37",
            cursor: "pointer",
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  if (!text || notFound) {
    return (
      <div style={{ color: "#D4AF37", padding: "40px", textAlign: "center" }}>
        <p>Artículo no encontrado.</p>
        <button
          type="button"
          onClick={() => navigate("/")}
          style={{
            marginTop: "16px",
            padding: "10px 20px",
            borderRadius: "8px",
            border: "1px solid rgba(212,175,55,0.4)",
            background: "rgba(212,175,55,0.15)",
            color: "#D4AF37",
            cursor: "pointer",
          }}
        >
          Volver a Hoy
        </button>
      </div>
    );
  }

  return (
    <div className="article-view-page" style={{ ...deskGlass.page, display: "flex", flexDirection: "column", minHeight: isMobile ? "calc(100dvh - 52px)" : "100%", flex: 1 }}>
      <BackgroundImage image={BACKGROUNDS.dashboard} />
      {isMobile && (
        <button
          type="button"
          onClick={goBack}
          aria-label="Volver"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            margin: "8px 12px 0",
            padding: "10px 12px",
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(212,175,55,0.25)",
            borderRadius: "8px",
            color: "#D4AF37",
            cursor: "pointer",
            fontSize: "0.9rem",
            alignSelf: "flex-start",
          }}
        >
          <ArrowLeft size={18} />
          Volver
        </button>
      )}
      <div style={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <TextReader
        isOpen={true}
        inline
        onClose={goBack}
        onEdit={() => {
          navigate("/editor", { state: {
            autoStart: false,
            deskId: text.deskId,
            drawerId: text.drawerId,
            notebookId: text.notebookId,
            textId: text.id,
            initialTitle: text.title,
            initialContent: text.content,
            initialImg: text.img,
            mode: "edit",
          }});
        }}
        textId={text.id}
        title={text.title}
        content={text.content || ""}
        img={text.img}
        slug={text.slug}
        deskSlug={text.deskSlug}
        drawerSlug={text.drawerSlug}
        notebookSlug={text.notebookSlug}
        updatedAt={text.updatedAt}
        translations={text.translations || {}}
        onSaveTranslation={handleSaveTranslation}
        deskId={text.deskId}
        drawerId={text.drawerId}
        notebookId={text.notebookId}
        isRead={text.isRead}
        onToggleRead={handleToggleRead}
        readingMode={readingMode}
        onReadingAction={handleReadingAction}
        queueMeta={queueMeta}
        kind={text.kind}
        onOpenChild={(child) => {
          navigateToText(navigate, {
            ...child,
            deskId: text.deskId,
            drawerId: text.drawerId,
            notebookId: text.notebookId,
            deskSlug: text.deskSlug,
            drawerSlug: text.drawerSlug,
            notebookSlug: text.notebookSlug,
          });
        }}
      />
      </div>
    </div>
  );
}
